import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { toTitleCase } from '../../lib/format'
import { Shell } from '../../components/Shell'
import { Player, Spinner, mmss } from '../../components/ui'
import MediaUpload from './MediaUpload'

/**
 * The vault — every recording this song has ever had.
 *
 * A song is not a file. It is worked on for months, sung on a Sunday, recorded
 * properly a year later, and each of those is the same song and a different
 * take. Uploading used to overwrite: the car voice memo that got the idea
 * across was gone the moment a better version existed.
 *
 * `song_recordings` keeps all of them and `songs.flagship_recording_id` says
 * which one *is* the song. The flagship writes its key back onto
 * `songs.r2_object_key`, so the reviewer, the church and the feed keep reading
 * the one column they always read and get the take the writer chose. Nothing
 * else in the app had to learn about this screen.
 */
export default function Vault() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user } = useSession()

  const [song, setSong] = useState(null)
  const [takes, setTakes] = useState([])
  const [src, setSrc] = useState({})      // recording id -> signed url
  const [loading, setLoading] = useState(null)
  const [busy, setBusy] = useState(null)
  const [editing, setEditing] = useState(null)
  const [draftLabel, setDraftLabel] = useState('')
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s }, { data: rs }] = await Promise.all([
      supabase.from('songs')
        .select(`id, title, songwriter_id, flagship_recording_id, r2_object_key,
                 media_mime, media_filename, duration_seconds`)
        .eq('id', id).maybeSingle(),
      supabase.from('song_recordings')
        .select('id, label, media_mime, media_filename, duration_seconds, created_at')
        .eq('song_id', id).is('removed_at', null)
        .order('created_at', { ascending: false }),
    ])
    setSong(s ?? null)
    setTakes(rs ?? [])
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  /** One take, signed on demand. The flagship could go through song_id, but
      asking by recording id keeps every row on this screen identical. */
  const hear = async (rec) => {
    if (src[rec.id]) return
    setLoading(rec.id)
    const { data, error } = await supabase.functions
      .invoke('r2-playback-url', { body: { recording_id: rec.id } })
    setLoading(null)
    if (error) return setErr(readableError(error))
    setSrc((m) => ({ ...m, [rec.id]: data?.url ?? null }))
  }

  const makeFlagship = async (rec) => {
    setBusy(rec.id); setErr(null)
    const { error } = await supabase.rpc('set_flagship_recording', { p_recording: rec.id })
    setBusy(null)
    if (error) return setErr(readableError(error))
    await load()
  }

  const remove = async (rec) => {
    setBusy(rec.id); setErr(null)
    const { error } = await supabase.rpc('remove_song_recording', { p_recording: rec.id })
    setBusy(null)
    if (error) return setErr(readableError(error))
    await load()
  }

  const saveLabel = async (rec) => {
    const label = draftLabel.trim() || null
    setEditing(null)
    if (label === (rec.label ?? null)) return
    setTakes((t) => t.map((x) => (x.id === rec.id ? { ...x, label } : x)))
    const { error } = await supabase.from('song_recordings')
      .update({ label }).eq('id', rec.id)
    if (error) { setErr(readableError(error)); load() }
  }

  if (!ready) return <Shell back={`/songs/${id}/share`} title="The vault"><Spinner /></Shell>

  if (!song || song.songwriter_id !== user.id) {
    return (
      <Shell back="/me" title="The vault" tabs>
        <h2 className="q">This isn't your song.</h2>
        <p className="sub">The vault is the writer's own record of it.</p>
      </Shell>
    )
  }

  const when = (t) => new Date(t).toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <Shell back={`/songs/${id}/share`} title="The vault" tabs>
      <div className="eyebrow">Every version of</div>
      <h2 className="q">{toTitleCase(song.title) || 'Untitled'}</h2>
      <p className="sub">
        A song changes. Keep the voice memo that got the idea across, the Sunday
        someone recorded it, and the proper version — and say which one is the song.
      </p>

      {err && <p className="err" style={{ marginTop: 12 }}>{err}</p>}

      <div className="hr" />
      <div className="eyebrow">
        {takes.length === 0 ? 'Nothing here yet'
          : takes.length === 1 ? 'One recording' : `${takes.length} recordings`}
      </div>

      {takes.length === 0 && (
        <p className="small">
          Add a recording below and it lands here. Every one you add afterwards
          joins it — nothing gets written over.
        </p>
      )}

      <div className="stack">
        {takes.map((rec) => {
          const isSong = rec.id === song.flagship_recording_id
          return (
            <div key={rec.id} className={`tile${isSong ? ' tile-on' : ''}`}>
              <div className="rowhead">
                {editing === rec.id ? (
                  <input
                    className="input" autoFocus value={draftLabel}
                    placeholder="What is this one?"
                    onChange={(e) => setDraftLabel(e.target.value)}
                    onBlur={() => saveLabel(rec)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  />
                ) : (
                  <button
                    className="linkish" style={{ fontWeight: 600, textAlign: 'left' }}
                    onClick={() => { setEditing(rec.id); setDraftLabel(rec.label ?? '') }}>
                    {rec.label || rec.media_filename || 'A recording'}
                  </button>
                )}
                {isSong && <span className="badge">The song</span>}
              </div>

              <p className="small" style={{ marginTop: 4 }}>
                {when(rec.created_at)}
                {rec.duration_seconds ? ` · ${mmss(rec.duration_seconds)}` : ''}
              </p>

              {src[rec.id]
                ? <Player src={src[rec.id]} mime={rec.media_mime}
                          duration={rec.duration_seconds} filename={rec.media_filename} />
                : (
                  <button className="btn btn-quiet" style={{ marginTop: 10 }}
                          disabled={loading === rec.id} onClick={() => hear(rec)}>
                    {loading === rec.id ? 'Getting it…' : 'Listen'}
                  </button>
                )}

              {!isSong && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                  <button className="btn btn-quiet" disabled={busy === rec.id}
                          onClick={() => makeFlagship(rec)}>
                    Make this the song
                  </button>
                  <button className="linkish" disabled={busy === rec.id}
                          onClick={() => remove(rec)}>
                    Take it out
                  </button>
                </div>
              )}
              {isSong && (
                <p className="small" style={{ marginTop: 8, opacity: 0.8 }}>
                  This is what a church hears, and what the clip is cut from.
                </p>
              )}
            </div>
          )
        })}
      </div>

      <div className="hr" />
      <div className="eyebrow">Add another</div>
      <p className="small" style={{ marginBottom: 10 }}>
        The newest one becomes the song. You can hand it back to an older take
        any time.
      </p>
      {/* Same uploader as the writing flow, on purpose — one upload path means
          one place where compression, duration and the musicality check live.
          It writes songs.r2_object_key and the trigger files it in the vault. */}
      <MediaUpload song={song} onChange={(patch) => {
        setSong((s) => ({ ...s, ...patch }))
        load()
      }} />

      <div className="hr" />
      <button className="linkish" onClick={() => nav(`/songs/${id}/share`)}>
        Back to who hears it
      </button>
    </Shell>
  )
}
